/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import clsx from 'clsx'
import {closeModal} from 'helpers/actions'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {ModalSize} from 'types/enums/ModalSize'
import UiDialogHeader from 'components/Forms/UiDialogHeader'
import UiFormBody from 'components/Forms/UiFormBody'

const Popup: React.FC = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const popup = useTypedSelector((state) => state.ui.modal)
    const blur = useTypedSelector((state) => state.ui.blur)
    const headbarTitle = useTypedSelector((state) => state.ui.headbarTitle)

    const isOpen = popup.isOpen || false
    const isCloseEnabled = popup.props?.isCloseEnabled === undefined ? true : popup.props.isCloseEnabled
    const popupSize = popup.props?.modalSize || ModalSize.FullScreen
    const closeModalCallback = popup.props?.closeModal

    const closePopup = useCallback(() => {
        dispatch(closeModal())
    }, [dispatch])

    const closeModalHandler = () => {
        if (closeModalCallback) {
            closeModalCallback()
            return
        }
        closePopup()
    }

    useEffect(() => {
        const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
        if (navigationEntries.length > 0 && navigationEntries[0].type === 'reload') {
            closePopup()
        }

        return () => undefined
    }, [closePopup])

    useEffect(() => {
        closePopup()
    }, [location.pathname, closePopup])

    if (!popup.component) {
        return null
    }

    if (!React.isValidElement(popup.component)) {
        return null
    }

    // RoundedFullscreen is rendered in Layout, not here
    if (popupSize === ModalSize.RoundedFullscreen) {
        return null
    }

    return (
        <div
            className={clsx(
                'Popup flex overscroll-contain fixed inset-0 z-50 w-full h-full justify-center items-center transform-gpu bg-gray-800/80',
                popupSize === ModalSize.FullScreen && 'w-screen h-screen min-h-screen',
                popupSize === ModalSize.FullHeight && 'top-0 bottom-0 h-screen min-h-screen w-auto py-2',
                popupSize === ModalSize.Small && 'px-2 sm:px-4 py-2 sm:py-4 overflow-hidden',
                popupSize === ModalSize.Medium && 'px-2 sm:px-4 py-2 sm:py-4 overflow-hidden',
                popupSize === ModalSize.Scrollable && 'px-2 sm:px-4 py-2 sm:py-4 overflow-hidden'
            )}
            style={{
                transition: '300ms',
                transform:
                    'translate3d(0px, 0px, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)',
                transformStyle: 'preserve-3d',
                opacity: isOpen ? 1 : 0,
                display: isOpen ? 'flex' : 'none',
            }}
        >
            <div
                className={clsx(
                    'BackdropInner fixed inset-0 flex justify-center items-center z-40',
                    blur &&
                        (popupSize === ModalSize.Small ||
                            popupSize === ModalSize.Medium ||
                            popupSize === ModalSize.Scrollable)
                        ? 'backdrop-blur-sm'
                        : ''
                )}
                id={'CloseFilterModal'}
                style={{ filter: blur ? 'blur(2px)' : undefined }}
            />
            <div
                className={clsx(
                    'PopupInner z-40 relative bg-white  dark:bg-gray-900',
                    popupSize === ModalSize.RoundedFullscreen &&
                        'rounded-tl-4xl  overflow-auto shadow-2xl w-[calc(100vw-7rem)] h-[calc(100vh-5rem)]',
                    popupSize === ModalSize.Small &&
                        'w-full max-w-md md:max-w-lg lg:max-w-3xl xl:max-w-5xl max-h-[70vh] overflow-hidden flex flex-col p-4 sm:p-6 rounded-lg mx-4 md:mx-8',
                    popupSize === ModalSize.Medium &&
                        'w-full max-w-[82vw] md:max-w-[82vw] max-h-[80vh] overflow-hidden flex flex-col rounded-lg h-auto relative mx-4 md:mx-8',
                    popupSize === ModalSize.FullScreen && 'w-full overflow-hidden h-full flex flex-col',
                    popupSize === ModalSize.FullHeight && 'w-auto overflow-hidden h-full flex flex-col',
                    popupSize === ModalSize.Scrollable &&
                        'flex flex-col w-full h-auto max-w-screen-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] overflow-hidden rounded-lg '
                )}
            >
                <UiDialogHeader
                    title={headbarTitle ?? undefined}
                    onClose={closeModalHandler}
                    showClose={isCloseEnabled && popupSize !== ModalSize.Scrollable}
                />
                <UiFormBody>
                    {popup.component}
                </UiFormBody>
            </div>
        </div>
    )
}

export default Popup
